import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { UserSearch } from 'lucide-react';
import { PLATFORM_LEAD_STATUSES, type PlatformLead, type PlatformLeadStatus } from '@worki/shared';
import { api, buildQuery } from '../../lib/api.js';
import { useDebounced } from '../../lib/useDebounced.js';
import { currentLocale } from '../../i18n.js';
import { formatDate } from '../../lib/format.js';
import { Button, EmptyState, Select, Spinner, Textarea } from '../../components/ui.js';
import { PageSearch } from '../../components/PageSearch.js';
import { Modal } from '../../components/Modal.js';
import { AdminCard, Pill, Row, Stat, useAdminAction } from './parts.js';

/**
 * ADM-006 — Worki's own pipeline.
 *
 * These are people who asked for an account, not a customer's enquiries: a
 * business's leads are its data and are never read from this screen. Everyone
 * who started the sign-up form is here, whether or not they finished it, which
 * is the one funnel the platform can honestly see.
 */
const TONES: Record<PlatformLeadStatus, 'info' | 'warn' | 'good' | 'neutral'> = {
  new: 'info',
  contacted: 'warn',
  converted: 'good',
  lost: 'neutral',
};

interface LeadPage {
  items: PlatformLead[];
  total: number;
  counts: Record<PlatformLeadStatus, number>;
}

export function LeadsSection() {
  const { t } = useTranslation();
  const locale = currentLocale();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [editing, setEditing] = useState<PlatformLead | null>(null);
  const debounced = useDebounced(search, 300);

  const list = useQuery({
    queryKey: ['platform', 'leads', { debounced, status }],
    queryFn: () => api.get<LeadPage>(`/platform/leads${buildQuery({ q: debounced, status, pageSize: 100 })}`),
  });

  const action = useAdminAction([
    ['platform', 'leads'],
    ['platform', 'notifications'],
  ]);

  const rows = list.data?.items ?? [];
  const counts = list.data?.counts;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {PLATFORM_LEAD_STATUSES.map((key) => (
          <Stat
            key={key}
            label={t(`admin.leads.status.${key}`)}
            value={counts?.[key] ?? 0}
            tone={key === 'converted' ? 'good' : key === 'new' ? 'info' : 'muted'}
          />
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <PageSearch value={search} onChange={setSearch} placeholder={t('admin.leads.searchPlaceholder')} />
        <Select value={status} aria-label={t('admin.leads.allStatuses')} onChange={setStatus} className="sm:w-52">
          <option value="">{t('admin.leads.allStatuses')}</option>
          {PLATFORM_LEAD_STATUSES.map((key) => (
            <option key={key} value={key}>
              {t(`admin.leads.status.${key}`)}
            </option>
          ))}
        </Select>
      </div>

      <p className="text-xs text-muted-foreground">{t('admin.leads.hint')}</p>

      {list.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState icon={<UserSearch className="h-7 w-7" />} title={t('admin.leads.empty')} />
      ) : (
        <div className="space-y-2">
          {rows.map((lead) => (
            <Row
              key={lead.id}
              icon={<UserSearch className="h-4 w-4" aria-hidden />}
              title={
                <span className="flex items-center gap-2">
                  <span className="truncate">{lead.businessName}</span>
                  <Pill tone={TONES[lead.status]}>{t(`admin.leads.status.${lead.status}`)}</Pill>
                </span>
              }
              subtitle={
                <span dir="ltr" className="inline-block max-w-full truncate">
                  {[lead.name, lead.phone, lead.email].filter(Boolean).join(' · ')}
                </span>
              }
              meta={formatDate(lead.createdAt, locale)}
              onClick={() => setEditing(lead)}
            />
          ))}
        </div>
      )}

      <EditLeadDialog
        lead={editing}
        busy={action.busy}
        onClose={() => setEditing(null)}
        onSave={(patch) => {
          const target = editing;
          if (!target) return;
          void action.run(() => api.patch(`/platform/leads/${target.id}`, patch), 'admin.leads.saved').then((ok) => {
            if (ok) setEditing(null);
          });
        }}
      />
    </div>
  );
}

function EditLeadDialog({
  lead,
  busy,
  onClose,
  onSave,
}: {
  lead: PlatformLead | null;
  busy: boolean;
  onClose: () => void;
  onSave: (patch: { status: PlatformLeadStatus; note: string | null }) => void;
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<PlatformLeadStatus>('new');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!lead) return;
    setStatus(lead.status);
    setNote(lead.note ?? '');
  }, [lead]);

  return (
    <Modal
      open={lead !== null}
      title={lead?.businessName ?? ''}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => onSave({ status, note: note.trim() === '' ? null : note.trim() })} loading={busy}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      {lead && (
        <div className="space-y-3">
          <AdminCard>
            <p className="text-sm font-medium">{lead.name}</p>
            <p className="text-xs text-muted-foreground" dir="ltr">
              {lead.email}
            </p>
            {lead.phone && (
              <p className="text-xs text-muted-foreground" dir="ltr">
                {lead.phone}
              </p>
            )}
            {lead.tradeName && (
              <p className="text-xs text-muted-foreground">
                {t('admin.leads.trade')}: {lead.tradeName}
              </p>
            )}
          </AdminCard>

          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">{t('admin.leads.status.new')}</span>
            <Select value={status} onChange={(value) => setStatus(value as PlatformLeadStatus)} aria-label={t('admin.leads.allStatuses')}>
              {PLATFORM_LEAD_STATUSES.map((key) => (
                <option key={key} value={key}>
                  {t(`admin.leads.status.${key}`)}
                </option>
              ))}
            </Select>
          </label>

          <label className="block space-y-1">
            <span className="text-xs font-medium text-muted-foreground">{t('admin.leads.note')}</span>
            <Textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={t('admin.leads.notePlaceholder')}
              rows={4}
            />
          </label>
        </div>
      )}
    </Modal>
  );
}
